import { useState, useMemo, useContext } from 'react'
import { Box, Typography, TextField, InputAdornment, Skeleton, IconButton, Pagination } from '@mui/material'
import { CalendarToday, PersonOutline, Search as SearchIcon } from '@mui/icons-material'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import AnnouncementDetailModal from '../components/AnnouncementDetailModal'
import DeleteAnnouncementModal from '../components/DeleteAnnouncementModal'
import EditAnnouncementModal from '../../dashboard/components/EditAnnouncementModal'
import { authContext } from 'contexts/AuthContext'
import { useGetAnnouncements, useDeleteAnnouncement } from 'services/api/announcements/announcementsHook'
import {
    canUserSeeAnnouncement,
    getAnnouncementUserDepartmentIds,
    getAnnouncementUserScopeKey,
} from 'services/api/announcements/visibility'

const formatPostedAt = (dateStr) => {
    if (!dateStr) return ''

    const posted = new Date(dateStr)
    if (Number.isNaN(posted.getTime())) return ''

    const now = new Date()

    // Compare by local calendar day (midnight boundaries), not elapsed hours.
    const postedDay = new Date(posted.getFullYear(), posted.getMonth(), posted.getDate())
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    const diffDays = Math.round((today - postedDay) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Posted today'
    if (diffDays === 1) return 'Posted 1 day ago'
    if (diffDays > 1 && diffDays <= 3) return `Posted ${diffDays} days ago`

    return `Posted at ${posted.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    })}`
}

const AnnouncementCard = ({ announcement, onReadMore, canCreate, onEdit, onDelete }) => {
    return (
        <Box
            sx={{
                border: '1px solid #E8E8E8',
                borderRadius: '8px',
                px: { xs: '16px', sm: '24px' },
                py: { xs: '16px', sm: '20px' },
                backgroundColor: '#FFFFFF',
                '&:hover': { borderColor: '#C8D8F0', backgroundColor: '#FAFCFF' },
                '&:hover .announcement-actions': { opacity: 1 },
                transition: 'all 0.15s ease',
            }}
        >
            {/* Title row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: '6px',
                    gap: '12px',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.4,
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-word',
                    }}
                >
                    {announcement.title}
                </Typography>

                {canCreate && (
                    <Box
                        className="announcement-actions"
                        sx={{ display: 'flex', gap: '4px', flexShrink: 0, ml: '12px', opacity: 0, transition: 'opacity 0.15s ease' }}
                    >
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onEdit && onEdit(announcement) }}
                            sx={{
                                color: '#3A3A3A',
                                px: 0.5,
                                py: 0.5,
                                bgcolor: '#ffffff',
                                borderRadius: 1,
                                border: '1px solid #E0E0E0',
                                '&:hover': { backgroundColor: '#FDF6E2', borderColor: '#F3BF3A' },
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 12 12" fill="none">
                                <path d="M3.3717 8.19432L5.4973 8.1871L10.1367 3.592C10.3188 3.40993 10.419 3.16813 10.419 2.91092C10.419 2.65371 10.3188 2.41192 10.1367 2.22985L9.37279 1.46592C9.00865 1.10178 8.37333 1.10371 8.01208 1.46448L3.3717 6.06054V8.19432ZM8.69171 2.147L9.45708 2.90948L8.68786 3.67147L7.92394 2.90803L8.69171 2.147ZM4.33504 6.46225L7.23949 3.58526L8.00341 4.34918L5.09944 7.22521L4.33504 7.22762V6.46225Z" fill="#3A3A3A" />
                                <path d="M2.40828 10.1153H9.15161C9.68289 10.1153 10.1149 9.68326 10.1149 9.15198V4.97689L9.15161 5.94023V9.15198H3.92938C3.91686 9.15198 3.90385 9.1568 3.89133 9.1568C3.87544 9.1568 3.85954 9.15246 3.84316 9.15198H2.40828V2.40865H5.70625L6.66958 1.44531H2.40828C1.877 1.44531 1.44495 1.87737 1.44495 2.40865V9.15198C1.44495 9.68326 1.877 10.1153 2.40828 10.1153Z" fill="#3A3A3A" />
                            </svg>
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onDelete && onDelete(announcement) }}
                            sx={{
                                color: '#3A3A3A',
                                px: 0.5,
                                py: 0.5,
                                bgcolor: '#ffffff',
                                borderRadius: 1,
                                border: '1px solid #E0E0E0',
                                '&:hover': { backgroundColor: '#FDF6E2', borderColor: '#F3BF3A' },
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 12 12" fill="none">
                                <path d="M9.42495 2.35711C9.54993 2.35711 9.66979 2.40676 9.75817 2.49514C9.84655 2.58351 9.8962 2.70338 9.8962 2.82836C9.8962 2.95334 9.84655 3.07321 9.75817 3.16158C9.66979 3.24996 9.54993 3.29961 9.42495 3.29961H8.9537L8.95228 3.33307L8.51261 9.49278C8.49568 9.73056 8.38928 9.9531 8.21483 10.1156C8.04038 10.278 7.81085 10.3684 7.57246 10.3684H3.73696C3.49857 10.3684 3.26904 10.278 3.09459 10.1156C2.92014 9.9531 2.81374 9.73056 2.79682 9.49278L2.35714 3.33354L2.3562 3.29961H1.88495C1.75996 3.29961 1.6401 3.24996 1.55172 3.16158C1.46335 3.07321 1.4137 2.95334 1.4137 2.82836C1.4137 2.70338 1.46335 2.58351 1.55172 2.49514C1.6401 2.40676 1.75996 2.35711 1.88495 2.35711H9.42495ZM8.00978 3.29961H3.30011L3.73743 9.42586H7.57246L8.00978 3.29961ZM6.59745 0.943359C6.72243 0.943359 6.84229 0.993009 6.93067 1.08139C7.01905 1.16976 7.0687 1.28963 7.0687 1.41461C7.0687 1.53959 7.01905 1.65946 6.93067 1.74783C6.84229 1.83621 6.72243 1.88586 6.59745 1.88586H4.71245C4.58746 1.88586 4.4676 1.83621 4.37922 1.74783C4.29085 1.65946 4.2412 1.53959 4.2412 1.41461C4.2412 1.28963 4.29085 1.16976 4.37922 1.08139C4.4676 0.993009 4.58746 0.943359 4.71245 0.943359H6.59745Z" fill="#3A3A3A" />
                            </svg>
                        </IconButton>
                    </Box>
                )}
            </Box>

            {/* Description */}
            {(announcement.body || announcement.description || announcement.content) && (
                    <Typography
                        sx={{
                            fontSize: '13px',
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: '14px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                        }}
                    >
                    {(announcement.body || announcement.description || announcement.content)
                        .replace(/<[^>]*>/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim()}
                </Typography>
            )}

            {/* Footer row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: '12px', sm: '16px' },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row'},
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: '10px', sm: '16px' },
                        flexWrap: 'wrap',
                        width: 1,
                        minWidth: 0,
                    }}
                >
                    {/* Posted date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                        <CalendarToday sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', wordBreak: 'break-word' }}>
                            {formatPostedAt(announcement.published_at || announcement.posted_at || announcement.created_at)}
                        </Typography>
                    </Box>

                    {/* Author / Department */}
                    {(announcement.author?.name || announcement.author || announcement.department) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1 }}>
                            <PersonOutline sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', wordBreak: 'break-word' }}>
                                {announcement.author?.name || announcement.author || announcement.department}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Read full announcement */}
                <Typography
                    onClick={() => onReadMore && onReadMore(announcement)}
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#2666BE',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': { color: '#1a4a8a' },
                        flexShrink: 0,
                        alignSelf: { xs: 'flex-start', sm: 'center' },
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                    }}
                >
                    Read full announcement
                </Typography>
            </Box>
        </Box>
    )
}

const AnnouncementCardSkeleton = () => (
    <Box
        sx={{
            border: '1px solid #E8E8E8',
            borderRadius: '8px',
            px: { xs: '16px', sm: '24px' },
            py: { xs: '16px', sm: '20px' },
            backgroundColor: '#FFFFFF',
        }}
    >
        <Skeleton variant="text" width="55%" height={22} sx={{ mb: '6px' }} />
        <Skeleton variant="text" width="100%" height={18} />
        <Skeleton variant="text" width="80%" height={18} sx={{ mb: '14px' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: '16px' }}>
                <Skeleton variant="text" width={100} height={16} />
                <Skeleton variant="text" width={80} height={16} />
            </Box>
            <Skeleton variant="text" width={130} height={16} />
        </Box>
    </Box>
)

const PER_PAGE = 12

const Announcements = () => {
    const { data: userData } = useContext(authContext).auth
    // const isAdminOrSuperAdmin = Boolean(userData?.super_admin || userData?.permissions?.length)
    const canManageAnnouncements = Boolean(
        userData?.super_admin ||
        userData?.permissions?.includes(21)
    )
    const userDepartmentIds = useMemo(() => getAnnouncementUserDepartmentIds(userData), [userData])
    const announcementScopeKey = useMemo(() => getAnnouncementUserScopeKey(userData), [userData])

    const [searchQuery, setSearchQuery] = useState('')
    const [page, setPage] = useState(1)
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null)
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [editTarget, setEditTarget] = useState(null)

    const { data: apiData, isLoading } = useGetAnnouncements(page, PER_PAGE, { scopeKey: announcementScopeKey })

    const { mutate: deleteAnnouncement, isLoading: deleteIsLoading } = useDeleteAnnouncement({
        onSuccess: () => setDeleteTarget(null),
        onError: (error) => console.error('Failed to delete announcement:', error),
    })

    const rawList = apiData?.data?.data || []
    const visibleAnnouncements = useMemo(
        () => rawList.filter((announcement) => canUserSeeAnnouncement(announcement, userDepartmentIds)),
        [rawList, userDepartmentIds]
    )
    const totalPages = apiData?.data?.meta?.last_page || 1
    const totalCount = apiData?.data?.meta?.total || visibleAnnouncements.length
    const displayedCount = visibleAnnouncements.length < rawList.length ? visibleAnnouncements.length : totalCount

    const filteredAnnouncements = useMemo(() => {
        if (!searchQuery.trim()) return visibleAnnouncements
        const q = searchQuery.toLowerCase()
        return visibleAnnouncements.filter(
            (a) =>
                (a.title || '').toLowerCase().includes(q) ||
                (a.author?.name || a.author || a.department || '').toLowerCase().includes(q)
        )
    }, [visibleAnnouncements, searchQuery])

    const handlePageChange = (_, value) => {
        setPage(value)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <DashboardContainer headerTitle="Announcement">
            {/* Scrollable wrapper */}
            <Box sx={{
                height: { xs: 'calc(100vh - 60px)', lg: 'calc(100vh - 107px)' },
                overflowY: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
            }}
            >
                <Box sx={{
                    width: '100%',
                    maxWidth: {
                        xs: '100%',
                        sm: '100%',
                        md: 860,
                        lg: 1000,
                        xl: 1200,
                    },
                    mx: 'auto',
                    px: { xs: 2, sm: 3, md: 0 },
                    mb: 4,
                }}>
                    {/* Page heading */}
                    <Box sx={{ mb: '28px' }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                fontSize: '1.5rem',
                            }}
                        >
                            All Announcements
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: '4px' }}>
                            {isLoading ? (
                                <Skeleton variant="text" width={140} height={18} />
                            ) : (
                                `${displayedCount} announcement${displayedCount !== 1 ? 's' : ''} found`
                            )}
                        </Typography>
                    </Box>

                    {/* Search */}
                    <TextField
                        fullWidth
                        placeholder="Search announcements..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: '24px',
                            p: { xs: '12px', sm: '17px' },
                            backgroundColor: '#FFFFFF',
                            borderRadius: '10px',
                            border: '1px solid #e5e5e5',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                backgroundColor: '#FFFFFF',
                                fontSize: '14px',
                                '& fieldset': { borderColor: '#e5e5e5' },
                                '&:hover fieldset': { borderColor: '#e5e5e5' },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#e5e5e5',
                                    borderWidth: '1px',
                                },
                            },
                            '& .MuiInputBase-input': {
                                py: '12px',
                            },
                        }}
                    />

                    {/* Announcement list */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {isLoading ? (
                            [1, 2, 3, 4, 5].map((i) => <AnnouncementCardSkeleton key={i} />)
                        ) : filteredAnnouncements.length > 0 ? (
                            filteredAnnouncements.map((announcement) => (
                                <AnnouncementCard
                                    key={announcement.id}
                                    announcement={announcement}
                                    onReadMore={setSelectedAnnouncement}
                                    // canCreate={isAdminOrSuperAdmin}
                                    canCreate={canManageAnnouncements}
                                    onEdit={(item) => setEditTarget(item)}
                                    onDelete={(item) => setDeleteTarget(item)}
                                />
                            ))
                        ) : (
                            <Box
                                sx={{
                                    border: '1px solid #E5e5e5',
                                    borderRadius: '8px',
                                    py: '60px',
                                    textAlign: 'center',
                                    backgroundColor: '#FFFFFF',
                                }}
                            >
                                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                                    No announcements found
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Pagination */}
                    {!isLoading && totalPages > 1 && !searchQuery.trim() && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '28px', mb: '8px' }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                shape="rounded"
                                sx={{
                                    '& .MuiPaginationItem-root': { fontSize: '13px' },
                                    '& .Mui-selected': {
                                        backgroundColor: '#F3BF3A !important',
                                        color: '#1A1A1A',
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
            <AnnouncementDetailModal
                open={!!selectedAnnouncement}
                announcement={selectedAnnouncement}
                onClose={() => setSelectedAnnouncement(null)}
            />
            <DeleteAnnouncementModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteAnnouncement(deleteTarget?.id)}
                isLoading={deleteIsLoading}
                announcementTitle={deleteTarget?.title}
            />
            <EditAnnouncementModal
                open={!!editTarget}
                onClose={() => setEditTarget(null)}
                announcement={editTarget}
            />
        </DashboardContainer>
    )
}

export default Announcements
